/**
 * DailyTimesheetModal Component
 * 
 * Daily timesheet entry for crew heads.
 * If the signed-in user is the head of a crew, they can add hours
 * for all crew members for a single day.
 * 
 * Features:
 * - Multiple op codes per crew member
 * - Different op codes for different members
 * - Add/remove entry rows as needed
 * - RnB, Meal$, Travel$ entered once per employee and distributed
 *   proportionally across their project rows by hours at submit time
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { required, sumGreaterThan, validateSchema, isValid } from '../../utils/validation';
import OpCodeDescriptionSelect from '../common/OpCodeDescriptionSelect';
import GroupedProjectSelect from '../common/GroupedProjectSelect';
import EmployeeAutocomplete from '../common/EmployeeAutocomplete';
import { useToast } from '../../hooks/useToast';
import TimeTrackerHelpModal from './TimeTrackerHelpModal';
import { buildProjectDisciplineOptions } from './projectDisciplineOptions';

/**
 * Distribute RnB, Meal$, and Travel$ across rows proportionally by hours
 * (reg + t12 + dbl). Rounding remainder goes to the highest-hours row.
 * If totalHours === 0 all allowances are set to 0.
 *
 * pinnedMap: { [entryId]: { rnb?, meals?, travel_2? } }
 * Rows with a pinned value for a given allowance type are reserved that exact
 * value and excluded from the proportional distribution of the remainder.
 */
const distributeAllowances = (rows, totalRnb, totalMeals, totalTravel, pinnedMap = {}) => {
  // Initialise result: pinned rows get their fixed value (number), un-pinned get null
  const result = rows.map(r => {
    const pin = pinnedMap[r.id] || {};
    return {
      ...r,
      rnb:      pin.rnb      !== undefined ? pin.rnb      : null,
      meals:    pin.meals    !== undefined ? pin.meals    : null,
      travel_2: pin.travel_2 !== undefined ? pin.travel_2 : null,
    };
  });

  ['rnb', 'meals', 'travel_2'].forEach(key => {
    const total = { rnb: totalRnb, meals: totalMeals, travel_2: totalTravel }[key];
    const pinnedSum = result.reduce((s, r) => s + (r[key] !== null ? r[key] : 0), 0);
    const remainder = Math.max(0, total - pinnedSum);
    const freeRows  = result.filter(r => r[key] === null);
    const freeHours = freeRows.reduce(
      (s, r) => s + (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0), 0
    );

    if (freeHours === 0) {
      freeRows.forEach(r => { r[key] = 0; });
    } else {
      freeRows.forEach(r => {
        const hrs = (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0);
        r[key] = Math.round(remainder * (hrs / freeHours) * 100) / 100;
      });
      // Rounding correction — apply remainder to highest-hours free row
      const diff = Math.round((remainder - freeRows.reduce((s, r) => s + r[key], 0)) * 100) / 100;
      if (diff !== 0 && freeRows.length > 0) {
        const maxIdx = freeRows.reduce((mi, r, i) => {
          const h  = (parseFloat(r.reg) || 0) + (parseFloat(r.t12) || 0) + (parseFloat(r.dbl) || 0);
          const mh = (parseFloat(freeRows[mi].reg) || 0) + (parseFloat(freeRows[mi].t12) || 0) + (parseFloat(freeRows[mi].dbl) || 0);
          return h > mh ? i : mi;
        }, 0);
        freeRows[maxIdx][key] = Math.round((freeRows[maxIdx][key] + diff) * 100) / 100;
      }
    }
  });

  return result;
};

// Empty entry template
const createEmptyEntry = (crewMembers = [], defaultProjectId = null, defaultJobNumber = null, defaultOptionKey = null) => ({
  id: Date.now() + Math.random(),
  emp_code: crewMembers.length === 1 ? crewMembers[0].emp_code : '',
  emp_name: crewMembers.length === 1 ? crewMembers[0].emp_name : '',
  trade: crewMembers.length === 1 ? crewMembers[0].trade : '',
  division: crewMembers.length === 1 ? crewMembers[0].division : '',
  project_id: defaultProjectId,
  job_number: defaultJobNumber,
  optionKey: defaultOptionKey,
  op_code: '',
  op_code_description: '',
  shift: 'D',
  reg: '',
  t12: '',
  dbl: '',
  height: '',
  rnb_pinned:      '',  // '' = un-pinned (distribute from Daily Allowances); number string = reserved for this row
  meals_pinned:    '',
  travel_2_pinned: '',
  comment:         '',
});

const DailyTimesheetModal = ({ 
  projectId: initialProjectId, 
  jobNumber: initialJobNumber,
  canUseWeeklyTimesheet = false,
  onClose, 
  onSubmitComplete 
}) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [myCrews, setMyCrews] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [entries, setEntries] = useState([]);
  // Per-employee allowances: { [emp_code]: { rnb, meals, travel_2 } }
  const [employeeAllowances, setEmployeeAllowances] = useState({});
  // Pre-calculated per-row distribution (display only): { [emp_code]: { [entry_id]: { rnb, meals, travel_2 } } }
  const [computedAllowances, setComputedAllowances] = useState({});
  // Tracks which entry rows have their comment textarea expanded
  const [expandedComments, setExpandedComments] = useState(new Set());
  const { toast, showToast } = useToast();
  
  const [projects, setProjects] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const isDirtyRef = React.useRef(false);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
      if (response.data.projects) {
        const sortedProjects = [...response.data.projects].sort((a, b) => {
          const numA = parseInt(a.job_number, 10);
          const numB = parseInt(b.job_number, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return (a.job_number || '').localeCompare(b.job_number || '');
        });
        setProjects(sortedProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [instance, accounts]);

  const projectDisciplineOptions = useMemo(
    () => buildProjectDisciplineOptions(projects),
    [projects]
  );

  const crewProjectOptions = useMemo(() => {
    if (!selectedCrew?.projects?.length) return projectDisciplineOptions;
    return projectDisciplineOptions.filter(opt =>
      selectedCrew.projects.some(cp =>
        cp.project_id === opt.project_id && cp.discipline_code === opt.discipline_code
      )
    );
  }, [selectedCrew, projectDisciplineOptions]);

  // Unique employees currently in entries (for the allowances section)
  const uniqueEmployees = useMemo(() => {
    const seen = new Set();
    const result = [];
    entries.forEach(e => {
      if (e.emp_code && !seen.has(e.emp_code)) {
        seen.add(e.emp_code);
        result.push({ emp_code: e.emp_code, emp_name: e.emp_name });
      }
    });
    return result;
  }, [entries]);

  const handleTransferCrew = async () => {
    if (!selectedCrew || !transferTarget?.emp_code) return;
    setTransferring(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT', `/crews/${selectedCrew.id}`,
        { head_emp_code: transferTarget.emp_code, head_emp_name: transferTarget.emp_name }
      );
      if (response.data.success) {
        showToast(`Crew transferred to ${transferTarget.emp_name}`, 'success');
        setShowTransfer(false);
        setShowTransferConfirm(false);
        setTransferTarget(null);
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || 'Failed to transfer crew');
      }
    } catch (error) {
      console.error('Error transferring crew:', error);
      showToast(error.message || 'Failed to transfer crew', 'error');
    } finally {
      setTransferring(false);
    }
  };

  // Build allowance map for a list of people
  const buildAllowanceMap = (people) => {
    const map = {};
    people.forEach(p => { map[p.emp_code] = { rnb: '', meals: '', travel_2: '' }; });
    return map;
  };

  const fetchMyCrews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/crews/my-crews');
      if (response.data.success) {
        setMyCrews(response.data.data || []);
        if (response.data.data?.length === 1) {
          const crew = response.data.data[0];
          const allPeople = [];
          if (crew.head_emp_code) {
            allPeople.push({
              emp_code: crew.head_emp_code,
              emp_name: crew.head_emp_name || '',
              trade: crew.head_trade || '',
              division: crew.head_division || ''
            });
          }
          if (crew.members && crew.members.length > 0) {
            crew.members.forEach(member => {
              if (member.emp_code !== crew.head_emp_code) allPeople.push(member);
            });
          }
          setSelectedCrew(crew);
          const autoProject = (crew.projects && crew.projects.length === 1) ? crew.projects[0] : null;
          if (allPeople.length > 0) {
            const newEntries = allPeople.map(person => ({
              id: Date.now() + Math.random(),
              emp_code: person.emp_code,
              emp_name: person.emp_name,
              trade: person.trade || '',
              division: person.division || '',
              project_id: autoProject ? autoProject.project_id : null,
              job_number: autoProject ? `${autoProject.job_number}${autoProject.discipline_code}` : null,
              optionKey: autoProject ? `${autoProject.project_id}-${autoProject.discipline_code}` : null,
              op_code: '',
              op_code_description: '',
              shift: 'D',
              reg: '',
              t12: '',
              dbl: '',
              height: '',
              rnb_pinned:      '',
              meals_pinned:    '',
              travel_2_pinned: '',
              comment:         '',
            }));
            setEntries(newEntries);
            setEmployeeAllowances(buildAllowanceMap(allPeople));
          } else {
            setEntries([createEmptyEntry([], null, null, null)]);
            setEmployeeAllowances({});
          }
        }
      }
    } catch (error) {
      console.error('Error fetching my crews:', error);
      showToast('Failed to load your crews', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchMyCrews(); }, [fetchMyCrews]);

  const handleEntryProjectSelect = (entryId, optionKey) => {
    const selected = projectDisciplineOptions.find(opt => opt.optionKey === optionKey);
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry;
      return {
        ...entry,
        project_id: selected?.project_id || null,
        job_number: selected?.display_value || null,
        optionKey: optionKey || null,
        op_code: '',
        op_code_description: ''
      };
    }));
  };

  const handleCrewSelect = (crew) => {
    setSelectedCrew(crew);
    const allPeople = [];
    if (crew.head_emp_code) {
      allPeople.push({
        emp_code: crew.head_emp_code,
        emp_name: crew.head_emp_name || '',
        trade: crew.head_trade || '',
        division: crew.head_division || ''
      });
    }
    if (crew.members && crew.members.length > 0) {
      crew.members.forEach(member => {
        if (member.emp_code !== crew.head_emp_code) allPeople.push(member);
      });
    }
    const autoProject = (crew.projects && crew.projects.length === 1) ? crew.projects[0] : null;
    if (allPeople.length > 0) {
      const newEntries = allPeople.map(person => ({
        id: Date.now() + Math.random(),
        emp_code: person.emp_code,
        emp_name: person.emp_name,
        trade: person.trade || '',
        division: person.division || '',
        project_id: autoProject ? autoProject.project_id : null,
        job_number: autoProject ? `${autoProject.job_number}${autoProject.discipline_code}` : null,
        optionKey: autoProject ? `${autoProject.project_id}-${autoProject.discipline_code}` : null,
        op_code: '',
        op_code_description: '',
        shift: 'D',
        reg: '',
        t12: '',
        dbl: '',
        height: '',
        rnb_pinned:      '',
        meals_pinned:    '',
        travel_2_pinned: '',
        comment:         '',
      }));
      setEntries(newEntries);
      setEmployeeAllowances(buildAllowanceMap(allPeople));
    } else {
      setEntries([createEmptyEntry([], null, null, null)]);
      setEmployeeAllowances({});
    }
  };

  const addEntry = () => {
    setEntries(prev => [...prev, createEmptyEntry(selectedCrew?.members, null, null)]);
  };

  const blankHours = {
    reg: '',
    t12: '',
    dbl: '',
    height: '',
    rnb_pinned: '',
    meals_pinned: '',
    travel_2_pinned: '',
    comment: '',
  };

  const insertAfter = (entryId, row) => {
    isDirtyRef.current = true;
    setEntries((prev) => {
      const index = prev.findIndex((entry) => entry.id === entryId);
      const next = [...prev];
      next.splice(index + 1, 0, { ...row, id: Date.now() + Math.random() });
      return next;
    });
  };

  const duplicateEmployee = (entry) => {
    insertAfter(entry.id, {
      ...entry,
      ...blankHours,
      op_code: '',
      op_code_description: '',
    });
  };

  const duplicateOpCode = (entry) => {
    insertAfter(entry.id, {
      ...entry,
      ...blankHours,
      emp_code: '',
      emp_name: '',
      trade: '',
      division: '',
    });
  };

  const removeEntry = (entryId) => {
    if (entries.length <= 1) return;
    setEntries(prev => prev.filter(e => e.id !== entryId));
  };

  const updateEntry = (entryId, field, value) => {
    isDirtyRef.current = true;
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry;
      return { ...entry, [field]: value };
    }));
  };

  const updateAllowance = (empCode, field, value) => {
    isDirtyRef.current = true;
    setEmployeeAllowances(prev => ({
      ...prev,
      [empCode]: { ...prev[empCode], [field]: value }
    }));
    // Clear computed for this employee so the row cells show "—" until recalculated
    setComputedAllowances(prev => { const n = { ...prev }; delete n[empCode]; return n; });
  };

  const handleClose = () => {
    if (isDirtyRef.current) {
      if (!window.confirm('You have unsaved changes. Close without submitting?')) return;
    }
    if (onClose) onClose();
  };

  // Called when user edits a per-row pinned allowance cell.
  // Stores the value on the entry and clears stale computed distribution.
  const handlePinnedAllowanceChange = (entryId, field, value, empCode) => {
    setEntries(prev => prev.map(e =>
      e.id === entryId ? { ...e, [field]: value } : e
    ));
    // Distribution is stale now — clear preview so user knows to recalculate
    setComputedAllowances(prev => { const n = { ...prev }; delete n[empCode]; return n; });
  };

  const handleCalculateAllowances = (empCode) => {
    const empEntries = entries.filter(e =>
      e.emp_code === empCode && e.project_id && e.op_code &&
      ((parseFloat(e.reg) || 0) + (parseFloat(e.t12) || 0) + (parseFloat(e.dbl) || 0)) > 0
    );
    if (empEntries.length === 0) {
      showToast('No entries with hours found for this employee', 'error');
      return;
    }
    // Build pinnedMap from per-row pinned fields
    const pinnedMap = {};
    empEntries.forEach(e => {
      const pin = {};
      if (e.rnb_pinned      !== '' && e.rnb_pinned      !== undefined) pin.rnb      = parseFloat(e.rnb_pinned)      || 0;
      if (e.meals_pinned    !== '' && e.meals_pinned    !== undefined) pin.meals    = parseFloat(e.meals_pinned)    || 0;
      if (e.travel_2_pinned !== '' && e.travel_2_pinned !== undefined) pin.travel_2 = parseFloat(e.travel_2_pinned) || 0;
      if (Object.keys(pin).length) pinnedMap[e.id] = pin;
    });
    const a = employeeAllowances[empCode] || {};
    const distributed = distributeAllowances(
      empEntries,
      parseFloat(a.rnb)      || 0,
      parseFloat(a.meals)    || 0,
      parseFloat(a.travel_2) || 0,
      pinnedMap
    );
    const computed = {};
    empEntries.forEach((entry, i) => {
      computed[entry.id] = { rnb: distributed[i].rnb, meals: distributed[i].meals, travel_2: distributed[i].travel_2 };
    });
    setComputedAllowances(prev => ({ ...prev, [empCode]: computed }));
  };

  const handleEmployeeSelect = (entryId, empCode) => {
    let person = null;
    if (selectedCrew?.head_emp_code === empCode) {
      person = {
        emp_code: selectedCrew.head_emp_code,
        emp_name: selectedCrew.head_emp_name,
        trade: selectedCrew.head_trade || '',
        division: selectedCrew.head_division || ''
      };
    } else {
      person = selectedCrew?.members?.find(m => m.emp_code === empCode);
    }
    if (person) {
      setEntries(prev => prev.map(entry => {
        if (entry.id !== entryId) return entry;
        return {
          ...entry,
          emp_code: person.emp_code,
          emp_name: person.emp_name,
          trade: person.trade || '',
          division: person.division || ''
        };
      }));
      // Ensure allowance entry exists for this employee
      if (!employeeAllowances[person.emp_code]) {
        setEmployeeAllowances(prev => ({
          ...prev,
          [person.emp_code]: { rnb: '', meals: '', travel_2: '' }
        }));
      }
    }
  };

  const handleOpCodeSelect = (entryId, opCodeData) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry;
      return {
        ...entry,
        op_code: opCodeData.op_code || '',
        op_code_description: opCodeData.op_description || opCodeData.op_code_description || ''
      };
    }));
  };

  const getEntryTotal = (entry) => {
    return (parseFloat(entry.reg) || 0) + (parseFloat(entry.t12) || 0) + (parseFloat(entry.dbl) || 0);
  };

  const getGrandTotals = () => {
    let reg = 0, t12 = 0, dbl = 0, height = 0;
    entries.forEach(entry => {
      reg    += parseFloat(entry.reg)    || 0;
      t12    += parseFloat(entry.t12)    || 0;
      dbl    += parseFloat(entry.dbl)    || 0;
      height += parseFloat(entry.height) || 0;
    });
    return { reg, t12, dbl, height, total: reg + t12 + dbl };
  };

  // Total allowances across all employees (for display in footer summary)
  const getAllowanceTotals = () => {
    let rnb = 0, meals = 0, travel_2 = 0;
    Object.values(employeeAllowances).forEach(a => {
      rnb      += parseFloat(a.rnb)      || 0;
      meals    += parseFloat(a.meals)    || 0;
      travel_2 += parseFloat(a.travel_2) || 0;
    });
    return { rnb, meals, travel_2 };
  };

  const getValidEntries = () => {
    return entries.filter(entry => {
      const hasEmployee = entry.emp_code;
      const hasProject  = entry.project_id;
      const hasOpCode   = entry.op_code;
      const hasHours    = getEntryTotal(entry) > 0;
      return hasEmployee && hasProject && hasOpCode && hasHours;
    });
  };

  // Modal-level validation errors (only active after first submit attempt)
  const modalErrors = useMemo(() => submitted ? validateSchema(
    { date: selectedDate, crew: selectedCrew },
    {
      date: [required('Select a date')],
      crew: [required('Select a crew')],
    }
  ) : {}, [submitted, selectedDate, selectedCrew]);

  // Per-row validation errors (only active after first submit attempt)
  const rowErrors = useMemo(() => {
    if (!submitted) return {};
    const errs = {};
    entries.forEach(entry => {
      if (!entry.emp_code) return; // skip completely blank rows
      const rowErrs = validateSchema(
        { project_id: entry.project_id, op_code: entry.op_code, hours: null },
        {
          project_id: [required('Project required')],
          op_code:    [required('Op code required')],
          hours:      [sumGreaterThan(['reg', 't12', 'dbl'], 0, 'Enter hours')],
        },
        entry
      );
      if (!isValid(rowErrs)) errs[entry.id] = rowErrs;
    });
    return errs;
  }, [submitted, entries]);

  const handleSubmit = async () => {
    setSubmitted(true);

    const currentModalErrors = validateSchema(
      { date: selectedDate, crew: selectedCrew },
      {
        date: [required('Select a date')],
        crew: [required('Select a crew')],
      }
    );
    if (!isValid(currentModalErrors)) {
      showToast('Please complete all required fields before submitting', 'error');
      return;
    }

    const validEntries = getValidEntries();
    const hasRowErrors = entries.some(entry => {
      if (!entry.emp_code) return false;
      const errs = validateSchema(
        { project_id: entry.project_id, op_code: entry.op_code, hours: null },
        {
          project_id: [required()],
          op_code:    [required()],
          hours:      [sumGreaterThan(['reg', 't12', 'dbl'], 0)],
        },
        entry
      );
      return !isValid(errs);
    });
    if (validEntries.length === 0 || hasRowErrors) {
      showToast('Please complete all required fields before submitting', 'error');
      return;
    }

    // Daily hour cap check — per employee total REG+T12+DBL must not exceed 24
    const dailyTotals = {};
    validEntries.forEach(e => {
      dailyTotals[e.emp_code] = (dailyTotals[e.emp_code] || 0) + getEntryTotal(e);
    });
    const overLimit = Object.entries(dailyTotals).find(([, h]) => h > 24);
    if (overLimit) {
      const [empCode, total] = overLimit;
      const empName = validEntries.find(e => e.emp_code === empCode)?.emp_name || empCode;
      showToast(`${empName} has ${total.toFixed(1)}h on ${selectedDate} — exceeds the 24h daily limit.`, 'error');
      return;
    }

    // Group valid entries by emp_code then distribute allowances proportionally
    const byEmp = {};
    validEntries.forEach(e => { (byEmp[e.emp_code] ??= []).push(e); });

    const distributedEntries = Object.entries(byEmp).flatMap(([empCode, empRows]) => {
      const a = employeeAllowances[empCode] || {};
      // Build pinnedMap from per-row pinned fields
      const pinnedMap = {};
      empRows.forEach(e => {
        const pin = {};
        if (e.rnb_pinned      !== '' && e.rnb_pinned      !== undefined) pin.rnb      = parseFloat(e.rnb_pinned)      || 0;
        if (e.meals_pinned    !== '' && e.meals_pinned    !== undefined) pin.meals    = parseFloat(e.meals_pinned)    || 0;
        if (e.travel_2_pinned !== '' && e.travel_2_pinned !== undefined) pin.travel_2 = parseFloat(e.travel_2_pinned) || 0;
        if (Object.keys(pin).length) pinnedMap[e.id] = pin;
      });
      return distributeAllowances(
        empRows,
        parseFloat(a.rnb)      || 0,
        parseFloat(a.meals)    || 0,
        parseFloat(a.travel_2) || 0,
        pinnedMap
      );
    });

    const apiEntries = distributedEntries.map(entry => ({
      date:                selectedDate,
      project_id:          entry.project_id,
      discipline_code:     entry.optionKey ? entry.optionKey.split('-')[1] : null,
      empl_code:           entry.emp_code,
      empl_name:           entry.emp_name,
      trade:               entry.trade,
      division:            entry.division,
      op_code:             entry.op_code,
      op_code_description: entry.op_code_description,
      shift:               entry.shift || 'D',
      reg:                 parseFloat(entry.reg)    || 0,
      t12:                 parseFloat(entry.t12)    || 0,
      dbl:                 parseFloat(entry.dbl)    || 0,
      height:              parseFloat(entry.height) || 0,
      rnb:                 entry.rnb,
      meals:               entry.meals,
      travel_2:            entry.travel_2,
      comment:             entry.comment || null,
    }));

    setSaving(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', '/actual-spent/daily-crew-entry', { entries: apiEntries }
      );
      if (response.data.success) {
        isDirtyRef.current = false;
        const savedCount = response.data.data?.saved?.length ?? apiEntries.length;
        const errorCount = response.data.data?.errors?.length ?? 0;
        if (errorCount > 0) {
          showToast(`Saved ${savedCount} of ${apiEntries.length} entries. ${errorCount} failed — check for duplicate project/op-code combinations.`, 'error');
        } else {
          showToast(`Saved ${savedCount} entries for ${selectedDate}`, 'success');
        }
        if (onSubmitComplete) onSubmitComplete();
      } else {
        throw new Error(response.data.message || 'Failed to save entries');
      }
    } catch (error) {
      console.error('Error saving daily entries:', error);
      showToast(error.response?.data?.message || error.message || 'Failed to save entries', 'error');
    } finally {
      setSaving(false);
    }
  };

  const grandTotals  = getGrandTotals();
  const allowTotals  = getAllowanceTotals();
  const validCount   = getValidEntries().length;
  const allRowsHaveOpCode = entries.every(entry => entry.op_code && entry.op_code.trim() !== '');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px',
          padding: '1rem 1.5rem', borderRadius: '8px',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white', zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        width: '95%', maxWidth: '1400px', maxHeight: '90vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem', borderBottom: '1px solid #dee2e6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
            📅 Daily Crew Timesheet
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              style={{ padding: '8px 12px', border: 'none', borderRadius: '4px', backgroundColor: '#0d9488', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
            >
              ❓ Help
            </button>
            <button
              onClick={handleClose}
              style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', backgroundColor: '#6c757d', color: 'white', cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>Loading your crews...</div>
          ) : myCrews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👤</div>
              <div style={{ fontSize: '1.1rem', color: '#333', marginBottom: '0.5rem' }}>You are not a crew head</div>
              <div style={{ color: '#6c757d', fontSize: '0.95rem' }}>
                The Daily sheet is for the person who heads a crew.
                <br /><br />
                Open Crews, create a crew with yourself as the head, and add your people. Then come back here.
                {canUseWeeklyTimesheet ? (
                  <>
                    <br /><br />
                    For your own week, use <strong>Weekly</strong>.
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {/* Date & Crew Selection */}
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '0.9rem' }}>Date <span style={{ color: '#dc3545' }}>*</span></label>
                  <input
                    type="date" value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${modalErrors.date ? '#dc3545' : '#dee2e6'}`, borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                  {modalErrors.date && (
                    <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: '3px' }}>{modalErrors.date}</div>
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Select Crew</label>
                    {selectedCrew && (
                      <button
                        onClick={() => { setShowTransfer(!showTransfer); setShowTransferConfirm(false); setTransferTarget(null); }}
                        style={{
                          padding: '4px 10px', border: '1px solid #ffc107', borderRadius: '4px',
                          backgroundColor: showTransfer ? '#ffc107' : 'white',
                          color: showTransfer ? '#333' : '#856404',
                          cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
                        }}
                      >
                        🔄 Transfer Crew
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {myCrews.map(crew => (
                      <button
                        key={crew.id}
                        onClick={() => handleCrewSelect(crew)}
                        style={{
                          padding: '10px 16px',
                          border: selectedCrew?.id === crew.id ? '2px solid #007bff' : (modalErrors.crew ? '1px solid #dc3545' : '1px solid #dee2e6'),
                          borderRadius: '6px',
                          backgroundColor: selectedCrew?.id === crew.id ? '#e3f2fd' : 'white',
                          color: '#333', cursor: 'pointer', fontSize: '0.9rem'
                        }}
                      >
                        <strong>{crew.crew_id}</strong>
                        {crew.crew_name && <span style={{ color: '#6c757d' }}> - {crew.crew_name}</span>}
                        <span style={{ marginLeft: '8px', backgroundColor: '#6c757d', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem' }}>
                          {crew.members?.length || 0}
                        </span>
                      </button>
                    ))}
                  </div>
                  {modalErrors.crew && (
                    <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: '4px' }}>{modalErrors.crew}</div>
                  )}

                  {/* Transfer Crew Panel */}
                  {showTransfer && selectedCrew && (
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                      <div style={{ marginBottom: '0.75rem', fontWeight: '500', color: '#856404' }}>
                        Transfer "{selectedCrew.crew_id}" to a new crew head:
                      </div>
                      {!showTransferConfirm ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '220px' }}>
                            <EmployeeAutocomplete
                              value={transferTarget}
                              onChange={(employee) => setTransferTarget(employee)}
                              placeholder="Search the new crew head..."
                            />
                          </div>
                          <button
                            onClick={() => { if (!transferTarget?.emp_code) { showToast('Please select an employee', 'error'); return; } setShowTransferConfirm(true); }}
                            disabled={!transferTarget?.emp_code}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', backgroundColor: transferTarget?.emp_code ? '#ffc107' : '#ccc', color: '#333', cursor: transferTarget?.emp_code ? 'pointer' : 'not-allowed', fontWeight: '500', fontSize: '0.9rem' }}
                          >
                            Transfer
                          </button>
                          <button
                            onClick={() => { setShowTransfer(false); setTransferTarget(null); }}
                            style={{ padding: '8px 16px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white', color: '#6c757d', cursor: 'pointer', fontSize: '0.9rem' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div style={{ marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#f8d7da', borderRadius: '4px', color: '#721c24', fontSize: '0.9rem' }}>
                            ⚠️ <strong>Warning:</strong> You are about to transfer this crew to{' '}
                            <strong>{transferTarget?.emp_name}</strong>.
                            You will no longer be the head of this crew and this modal will close.
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={handleTransferCrew} disabled={transferring}
                              style={{ padding: '8px 20px', border: 'none', borderRadius: '4px', backgroundColor: transferring ? '#6c757d' : '#dc3545', color: 'white', cursor: transferring ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.9rem' }}
                            >
                              {transferring ? 'Transferring...' : 'Confirm Transfer'}
                            </button>
                            <button
                              onClick={() => setShowTransferConfirm(false)} disabled={transferring}
                              style={{ padding: '8px 16px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white', color: '#6c757d', cursor: 'pointer', fontSize: '0.9rem' }}
                            >
                              Go Back
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Info Banner */}
              {selectedCrew && (
                <div style={{ backgroundColor: '#e7f3ff', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem', color: '#0056b3' }}>
                  💡 <strong>Tip:</strong> The plus beside an employee copies that person onto a new row with no operation code.
                  The plus beside an operation code copies that code onto a new row with no employee.
                  Enter RnB, Meal$, and Travel$ totals per employee in the Allowances section below, then click <strong>Calculate</strong>.
                </div>
              )}

              {/* Entries Table */}
              {selectedCrew && selectedCrew.members?.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>
                      Time Entries ({entries.length} row{entries.length !== 1 ? 's' : ''})
                    </label>
                    <button
                      onClick={addEntry}
                      style={{ padding: '6px 12px', border: 'none', borderRadius: '4px', backgroundColor: '#28a745', color: 'white', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      + Add Row
                    </button>
                  </div>

                  <div style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#343a40', color: 'white' }}>
                          <th style={{ padding: '10px 8px', textAlign: 'left', width: '180px' }}>Employee</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', width: '200px' }}>Project #</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', width: '200px' }}>Op Description</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', width: '120px' }}>Op Code</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '55px' }}>Shift</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '60px', backgroundColor: '#28a745' }}>REG</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '60px', backgroundColor: '#ffc107', color: '#333' }}>T12</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '60px', backgroundColor: '#dc3545' }}>DBL</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '60px', backgroundColor: '#fd7e14' }} title="Height Pay">HIGH</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '55px' }} title="Hours Total (REG + T12 + DBL)">Hrs</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '70px', backgroundColor: '#0d9aaa', color: 'white', fontSize: '0.8rem' }} title="Leave blank to auto-distribute from Daily Allowances; enter a value to pin it to this row">RnB $</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '70px', backgroundColor: '#17a875', color: 'white', fontSize: '0.8rem' }} title="Leave blank to auto-distribute from Daily Allowances; enter a value to pin it to this row">Meal $</th>
                          <th style={{ padding: '10px 8px', textAlign: 'center', width: '70px', backgroundColor: '#c7206e', color: 'white', fontSize: '0.8rem' }} title="Leave blank to auto-distribute from Daily Allowances; enter a value to pin it to this row">Travel $</th>
                          <th style={{ padding: '10px 8px', width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, idx) => (
                          <React.Fragment key={entry.id}>
                          <tr style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa' }}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <select
                                value={entry.emp_code}
                                onChange={(e) => handleEmployeeSelect(entry.id, e.target.value)}
                                style={{ flex: 1, minWidth: 0, padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                              >
                                <option value="">Select employee...</option>
                                {selectedCrew.head_emp_code && (
                                  <option key={selectedCrew.head_emp_code} value={selectedCrew.head_emp_code}>
                                    {selectedCrew.head_emp_name} ({selectedCrew.head_emp_code}) - Head
                                  </option>
                                )}
                                {selectedCrew.members
                                  .filter(member => member.emp_code !== selectedCrew.head_emp_code)
                                  .map(member => (
                                    <option key={member.emp_code} value={member.emp_code}>
                                      {member.emp_name} ({member.emp_code})
                                    </option>
                                  ))}
                              </select>
                              <button
                                type="button"
                                title="New row for this employee, with no operation code"
                                aria-label="Copy this employee to a new row"
                                onClick={() => duplicateEmployee(entry)}
                                style={{ flex: '0 0 22px', width: 22, height: 22, padding: 0, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: '20px' }}
                              >
                                +
                              </button>
                              </div>
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', outline: rowErrors[entry.id]?.project_id ? '2px solid #dc3545' : 'none', borderRadius: '4px' }}>
                              <GroupedProjectSelect
                                value={entry.optionKey}
                                onChange={(optionKey) => handleEntryProjectSelect(entry.id, optionKey)}
                                options={crewProjectOptions}
                                placeholder="Select project..."
                                style={{ minWidth: '180px' }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', outline: rowErrors[entry.id]?.op_code ? '2px solid #dc3545' : 'none', borderRadius: '4px' }}>
                              <OpCodeDescriptionSelect
                                value={{ op_code: entry.op_code, op_description: entry.op_code_description }}
                                onChange={(data) => handleOpCodeSelect(entry.id, data)}
                                jobId={entry.project_id}
                                disciplineCode={entry.optionKey ? entry.optionKey.split('-')[1] : null}
                                displayField="op_description"
                                placeholder="Search description..."
                              />
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', outline: rowErrors[entry.id]?.op_code ? '2px solid #dc3545' : 'none', borderRadius: '4px', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                              <OpCodeDescriptionSelect
                                value={{ op_code: entry.op_code, op_description: entry.op_code_description }}
                                onChange={(data) => handleOpCodeSelect(entry.id, data)}
                                jobId={entry.project_id}
                                disciplineCode={entry.optionKey ? entry.optionKey.split('-')[1] : null}
                                displayField="op_code"
                                placeholder="Search code..."
                              />
                              </div>
                              <button
                                type="button"
                                title="New row with this operation code, and no employee"
                                aria-label="Copy this operation code to a new row"
                                onClick={() => duplicateOpCode(entry)}
                                style={{ flex: '0 0 22px', width: 22, height: 22, padding: 0, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: '20px' }}
                              >
                                +
                              </button>
                              </div>
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee' }}>
                              <select
                                value={entry.shift || 'D'}
                                onChange={(e) => updateEntry(entry.id, 'shift', e.target.value)}
                                style={{ width: '100%', padding: '8px 4px', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'center', fontSize: '0.85rem', boxSizing: 'border-box', backgroundColor: entry.shift === 'N' ? '#f3e8ff' : entry.shift === 'A' ? '#fff8e1' : 'white' }}
                                title="Shift: D=Day (0), A=Afternoon (1), N=Night (2)"
                              >
                                <option value="D">D</option>
                                <option value="A">A</option>
                                <option value="N">N</option>
                              </select>
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee' }}>
                              <input type="number" step="0.5" min="0" max="24" value={entry.reg}
                                onChange={(e) => updateEntry(entry.id, 'reg', e.target.value)}
                                style={{ width: '100%', padding: '8px 4px', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box' }} />
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee' }}>
                              <input type="number" step="0.5" min="0" max="24" value={entry.t12}
                                onChange={(e) => updateEntry(entry.id, 't12', e.target.value)}
                                style={{ width: '100%', padding: '8px 4px', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box' }} />
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee' }}>
                              <input type="number" step="0.5" min="0" max="24" value={entry.dbl}
                                onChange={(e) => updateEntry(entry.id, 'dbl', e.target.value)}
                                style={{ width: '100%', padding: '8px 4px', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box' }} />
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee' }}>
                              <input type="number" step="0.5" min="0" max="24" value={entry.height}
                                onChange={(e) => updateEntry(entry.id, 'height', e.target.value)}
                                style={{ width: '100%', padding: '8px 4px', border: '1px solid #dee2e6', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box' }} />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'center', fontWeight: '600', backgroundColor: getEntryTotal(entry) > 0 ? '#d4edda' : (rowErrors[entry.id]?.hours ? '#fee2e2' : 'transparent'), outline: rowErrors[entry.id]?.hours ? '2px solid #dc3545' : 'none' }}>
                              {getEntryTotal(entry).toFixed(1)}
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee', backgroundColor: '#f0fbfd' }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={entry.rnb_pinned}
                                onChange={(e) => handlePinnedAllowanceChange(entry.id, 'rnb_pinned', e.target.value, entry.emp_code)}
                                placeholder={
                                  computedAllowances[entry.emp_code]?.[entry.id] !== undefined
                                    ? computedAllowances[entry.emp_code][entry.id].rnb.toFixed(2)
                                    : '—'
                                }
                                title={entry.rnb_pinned !== '' ? '🔒 Pinned — excluded from auto-distribution' : 'Blank = auto-distributed from Daily Allowances'}
                                style={{
                                  width: '100%', padding: '6px 4px', boxSizing: 'border-box',
                                  border: entry.rnb_pinned !== '' ? '2px solid #0d9aaa' : '1px solid #b2ebf2',
                                  borderRadius: '4px', textAlign: 'center', fontSize: '0.82rem',
                                  fontWeight: entry.rnb_pinned !== '' ? '600' : '400',
                                  backgroundColor: entry.rnb_pinned !== '' ? '#e0f7fa' : 'transparent',
                                  color: '#0c7a8e',
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee', backgroundColor: '#f0fdf8' }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={entry.meals_pinned}
                                onChange={(e) => handlePinnedAllowanceChange(entry.id, 'meals_pinned', e.target.value, entry.emp_code)}
                                placeholder={
                                  computedAllowances[entry.emp_code]?.[entry.id] !== undefined
                                    ? computedAllowances[entry.emp_code][entry.id].meals.toFixed(2)
                                    : '—'
                                }
                                title={entry.meals_pinned !== '' ? '🔒 Pinned — excluded from auto-distribution' : 'Blank = auto-distributed from Daily Allowances'}
                                style={{
                                  width: '100%', padding: '6px 4px', boxSizing: 'border-box',
                                  border: entry.meals_pinned !== '' ? '2px solid #17a875' : '1px solid #b2dfdb',
                                  borderRadius: '4px', textAlign: 'center', fontSize: '0.82rem',
                                  fontWeight: entry.meals_pinned !== '' ? '600' : '400',
                                  backgroundColor: entry.meals_pinned !== '' ? '#e8f5e9' : 'transparent',
                                  color: '#147a58',
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px', borderBottom: '1px solid #eee', backgroundColor: '#fdf0f7' }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={entry.travel_2_pinned}
                                onChange={(e) => handlePinnedAllowanceChange(entry.id, 'travel_2_pinned', e.target.value, entry.emp_code)}
                                placeholder={
                                  computedAllowances[entry.emp_code]?.[entry.id] !== undefined
                                    ? computedAllowances[entry.emp_code][entry.id].travel_2.toFixed(2)
                                    : '—'
                                }
                                title={entry.travel_2_pinned !== '' ? '🔒 Pinned — excluded from auto-distribution' : 'Blank = auto-distributed from Daily Allowances'}
                                style={{
                                  width: '100%', padding: '6px 4px', boxSizing: 'border-box',
                                  border: entry.travel_2_pinned !== '' ? '2px solid #c7206e' : '1px solid #f8bbd0',
                                  borderRadius: '4px', textAlign: 'center', fontSize: '0.82rem',
                                  fontWeight: entry.travel_2_pinned !== '' ? '600' : '400',
                                  backgroundColor: entry.travel_2_pinned !== '' ? '#fce4ec' : 'transparent',
                                  color: '#9c1556',
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px 4px', borderBottom: '1px solid #eee', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button
                                title={entry.comment ? 'Comment added' : 'Add comment'}
                                onClick={() => setExpandedComments(prev => {
                                  const next = new Set(prev);
                                  next.has(entry.id) ? next.delete(entry.id) : next.add(entry.id);
                                  return next;
                                })}
                                style={{
                                  padding: '3px 6px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem',
                                  backgroundColor: entry.comment ? '#fef3c7' : '#f3f4f6',
                                  color: entry.comment ? '#92400e' : '#6b7280',
                                  marginRight: '4px',
                                }}>
                                💬{entry.comment ? '●' : ''}
                              </button>
                              {entries.length > 1 && (
                                <button onClick={() => removeEntry(entry.id)}
                                  style={{ padding: '4px 8px', border: 'none', borderRadius: '4px', backgroundColor: '#dc3545', color: 'white', cursor: 'pointer', fontSize: '0.75rem' }}>
                                  ✕
                                </button>
                              )}
                            </td>
                          </tr>
                          {expandedComments.has(entry.id) && (
                            <tr>
                              <td colSpan={15} style={{ padding: '0 8px 6px 12px', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                                <input
                                  type="text"
                                  maxLength={500}
                                  placeholder="Add a comment for this row…"
                                  value={entry.comment}
                                  onChange={(e) => updateEntry(entry.id, 'comment', e.target.value)}
                                  style={{
                                    width: '100%', padding: '5px 8px',
                                    border: '1px solid #fcd34d', borderRadius: '4px',
                                    fontSize: '0.83rem', backgroundColor: '#fffff8',
                                    boxSizing: 'border-box',
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                        {/* Totals Row */}
                        <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                          <td colSpan={5} style={{ padding: '10px 8px', textAlign: 'right' }}>
                            Totals ({validCount} valid entries):
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#28a745' }}>{grandTotals.reg.toFixed(1)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#b8860b' }}>{grandTotals.t12.toFixed(1)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#dc3545' }}>{grandTotals.dbl.toFixed(1)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#fd7e14' }}>{grandTotals.height.toFixed(1)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', backgroundColor: '#28a745', color: 'white' }}>{grandTotals.total.toFixed(1)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#0c7a8e', fontSize: '0.8rem' }}>{allowTotals.rnb.toFixed(2)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#147a58', fontSize: '0.8rem' }}>{allowTotals.meals.toFixed(2)}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center', color: '#9c1556', fontSize: '0.8rem' }}>{allowTotals.travel_2.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Employee Allowances Section */}
                  {uniqueEmployees.length > 0 && (
                    <div style={{ marginTop: '1.25rem', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ padding: '8px 14px', backgroundColor: '#343a40', color: 'white', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Daily Allowances</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#adb5bd' }}>
                          — enter totals per employee, then click Calculate to preview distribution across project rows
                        </span>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', width: '220px', borderBottom: '1px solid #dee2e6' }}>Employee</th>
                            <th style={{ padding: '8px', textAlign: 'center', width: '130px', borderBottom: '1px solid #dee2e6', color: '#17a2b8' }}>RnB $</th>
                            <th style={{ padding: '8px', textAlign: 'center', width: '130px', borderBottom: '1px solid #dee2e6', color: '#20c997' }}>Meal $</th>
                            <th style={{ padding: '8px', textAlign: 'center', width: '130px', borderBottom: '1px solid #dee2e6', color: '#e83e8c' }}>Travel $</th>
                            <th style={{ padding: '8px', textAlign: 'center', width: '120px', borderBottom: '1px solid #dee2e6' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {uniqueEmployees.map((emp, idx) => (
                            <tr key={emp.emp_code} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa' }}>
                              <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                                <span style={{ fontWeight: '500' }}>{emp.emp_name}</span>
                                <span style={{ color: '#6c757d', fontSize: '0.8rem', marginLeft: '6px' }}>({emp.emp_code})</span>
                                {computedAllowances[emp.emp_code] && (
                                  <span style={{ display: 'inline-block', marginLeft: '8px', padding: '1px 6px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '10px', fontSize: '0.7rem' }}>✓ calculated</span>
                                )}
                              </td>
                              <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                                <input type="number" step="0.01" min="0"
                                  value={employeeAllowances[emp.emp_code]?.rnb || ''}
                                  onChange={(e) => updateAllowance(emp.emp_code, 'rnb', e.target.value)}
                                  placeholder="0.00"
                                  style={{ width: '100%', padding: '8px 6px', border: '1px solid #b2ebf2', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box', fontSize: '0.85rem' }}
                                />
                              </td>
                              <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                                <input type="number" step="0.01" min="0"
                                  value={employeeAllowances[emp.emp_code]?.meals || ''}
                                  onChange={(e) => updateAllowance(emp.emp_code, 'meals', e.target.value)}
                                  placeholder="0.00"
                                  style={{ width: '100%', padding: '8px 6px', border: '1px solid #b2dfdb', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box', fontSize: '0.85rem' }}
                                />
                              </td>
                              <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                                <input type="number" step="0.01" min="0"
                                  value={employeeAllowances[emp.emp_code]?.travel_2 || ''}
                                  onChange={(e) => updateAllowance(emp.emp_code, 'travel_2', e.target.value)}
                                  placeholder="0.00"
                                  style={{ width: '100%', padding: '8px 6px', border: '1px solid #f8bbd0', borderRadius: '4px', textAlign: 'center', boxSizing: 'border-box', fontSize: '0.85rem' }}
                                />
                              </td>
                              <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleCalculateAllowances(emp.emp_code)}
                                  style={{ padding: '7px 14px', border: 'none', borderRadius: '4px', backgroundColor: '#17a2b8', color: 'white', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}
                                  title="Distribute allowances proportionally across this employee's project rows by hours"
                                >
                                  Calculate
                                </button>
                              </td>
                            </tr>
                          ))}
                          {/* Allowances Totals */}
                          <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                            <td style={{ padding: '8px 12px' }}>Totals:</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#17a2b8' }}>{allowTotals.rnb.toFixed(2)}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#20c997' }}>{allowTotals.meals.toFixed(2)}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: '#e83e8c' }}>{allowTotals.travel_2.toFixed(2)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add Row Button at bottom */}
                  <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={addEntry}
                      style={{ padding: '10px 24px', border: '1px solid #28a745', borderRadius: '4px', backgroundColor: 'white', color: '#28a745', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      + Add Another Row
                    </button>
                  </div>
                </div>
              )}

              {selectedCrew && (!selectedCrew.members || selectedCrew.members.length === 0) && (
                <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#fff3cd', borderRadius: '8px', color: '#856404' }}>
                  This crew has no members. Add members in Crew Management first.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {myCrews.length > 0 && selectedCrew && (
          <div style={{ borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
            {/* Validation summary banner */}
            {submitted && Object.keys(rowErrors).length > 0 && (
              <div style={{
                padding: '0.6rem 1.5rem',
                backgroundColor: '#fee2e2', color: '#991b1b',
                fontSize: '0.82rem', borderBottom: '1px solid #fecaca',
                display: 'flex', alignItems: 'center', gap: '0.4rem'
              }}>
                <span style={{ fontWeight: '600' }}>⚠</span>
                {Object.keys(rowErrors).length} row(s) are missing required fields — check highlighted cells above
              </div>
            )}
            <div style={{ padding: '1rem 1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={handleClose}
                style={{ padding: '10px 24px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: 'white', color: '#333', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  padding: '10px 24px', border: 'none', borderRadius: '4px',
                  backgroundColor: saving ? '#6c757d' : '#28a745',
                  color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem', fontWeight: '600'
                }}
              >
                {saving ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        )}
      </div>
      {showHelp && <TimeTrackerHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default DailyTimesheetModal;
